/**
 * CABPage — Main page for the Common Application Blueprint mode.
 *
 * Split layout:
 * - Left panel: File upload area + file list + action bar
 * - Right panel: Preview (empty state initially, later HTML preview)
 * - Progress sidebar: Vertical step progress (visible during generation)
 *
 * Integrates WebSocket for real-time progress updates.
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Text, Icon, Modal } from '@ui-library';
import { IconName } from '@ui-library/Icon/Icon.type';
import { Button } from '../components/Button/Button';
import { CABFileUpload } from '../components/CAB/CABFileUpload';
import { CABFileList } from '../components/CAB/CABFileList';
import { CABEmptyState } from '../components/CAB/CABEmptyState';
import { CABProgressSidebar } from '../components/CAB/CABProgressSidebar';
import { CABActionBar } from '../components/CAB/CABActionBar';
import { CABSectionSelector, DEFAULT_ENABLED_SECTIONS } from '../components/CAB/CABSectionSelector';
import { CABModelSelector, DEFAULT_MODEL_TIER } from '../components/CAB/CABModelSelector';
import type { CABModelTier } from '../components/CAB/CABModelSelector';
import { useCABWebSocket } from '../hooks/useCABWebSocket';
import { CABErrorState } from '../components/CAB/CABErrorState';
import { CABCostPanel } from '../components/CAB/CABCostPanel';
import { CABShareModal } from '../components/CAB/CABShareModal';
import { IfHasPermission } from '../components/IfHasPermission';
import { FormAlert } from '../components/FormAlert/FormAlert';
import { Permissions } from '../constants';
import {
  uploadCABFile,
  listCABFiles,
  deleteCABFile,
  downloadCABFile,
  getCABStatus,
  getCABProgress,
  getCABHtml,
  generateCAB,
  deleteCAB,
  pauseCAB,
  cancelCAB,
  resumeCAB,
  regenerateCAB,
  downloadCABMarkdown,
  downloadCABPdf,
  updateCABShareSettings,
  getCABVersion,
  getCABHtmlEditable,
  patchCAB,
  listCABVersions,
  getCABVersionHtml,
  restoreCABVersion,
} from '../services/cabApi';
import type { CABVersion } from '../services/cabApi';
import { CABVersionHistory } from '../components/CAB/CABVersionHistory';
import type {
  CABFile,
  CABSessionStatus,
  CABPhasesInfo,
  CABErrorInfo,
  CABCostInfo,
  FileUploadState,
  PatchOp,
  CABIframeMessage,
} from '../types/cab';
import { PlatformType } from '../types/project';
import { validateCABFilesForUpload } from '../utils/fileUploadConfig';
import { generateTicketsFromCAB, getCabTicketCount } from '../services/ticketApi';



interface CABPageProps {
  onProjectCostUpdate?: () => void;
  permissions?: string[];
  platformType?: PlatformType;
  onGenerateDesign?: () => void;
  onNavigateToTickets?: () => void;
}

const CABPage: React.FC<CABPageProps> = ({ onProjectCostUpdate, permissions = [], platformType, onGenerateDesign, onNavigateToTickets }) => {
  const { projectId } = useParams<{ projectId: string }>();

  // ────────────────────────────────────────────────────────────
  // STATE
  // ────────────────────────────────────────────────────────────

  const [files, setFiles] = useState<CABFile[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState<FileUploadState[]>([]);
  const [sessionStatus, setSessionStatus] = useState<CABSessionStatus>('idle');
  const [phases, setPhases] = useState<CABPhasesInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorInfo, setErrorInfo] = useState<CABErrorInfo | null>(null);
  const [currentProgressStage, setCurrentProgressStage] = useState<string | null>(null);
  const [overallPercentage, setOverallPercentage] = useState(0);
  const [costInfo, setCostInfo] = useState<CABCostInfo | null>(null);
  const [totalDurationSeconds, setTotalDurationSeconds] = useState<number | null>(null);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const [isDownloadingMarkdown, setIsDownloadingMarkdown] = useState(false);
  const [shareableLinkEnabled, setShareableLinkEnabled] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [enabledSections, setEnabledSections] = useState<string[]>(DEFAULT_ENABLED_SECTIONS);
  const [modelTier, setModelTier] = useState<CABModelTier>(DEFAULT_MODEL_TIER);

  // Guard against stale preview responses: each loadPreview call gets a
  // sequence number. Only the response from the latest call is applied.
  const previewSeqRef = useRef(0);

  // Edit mode state
  const [isEditMode, setIsEditMode] = useState(false);
  const [editableHtml, setEditableHtml] = useState<string | null>(null);
  const [isLoadingEditable, setIsLoadingEditable] = useState(false);
  // Stays true from the moment the user clicks Edit until the editable
  // iframe has fully parsed the HTML, applied edit-mode styles and signalled
  // `cab-iframe-ready`. Without this, the overlay would disappear right
  // after the network fetch resolves and the user would stare at a blank
  // (or stale) iframe for the multi-second parse with no feedback.
  const [isEnteringEditMode, setIsEnteringEditMode] = useState(false);
  // Stays true while we tear down edit mode (Discard) and the iframe is
  // re-rendering the read-only preview HTML, until `iframe.onload` fires.
  const [isExitingEditMode, setIsExitingEditMode] = useState(false);
  const exitTimeoutRef = useRef<number | null>(null);
  const [cabVersion, setCabVersion] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [toastMessage, setToastMessage] = useState<{ message: string; variant: 'success' | 'error' } | null>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Version history state
  const [versions, setVersions] = useState<CABVersion[]>([]);
  const [selectedVersionTs, setSelectedVersionTs] = useState<number | null>(null);
  const [versionHtml, setVersionHtml] = useState<string | null>(null);
  const [isLoadingVersionHtml, setIsLoadingVersionHtml] = useState(false);
  const [restoreTarget, setRestoreTarget] = useState<number | null>(null);
  const [isRestoring, setIsRestoring] = useState(false);
  const versionHtmlSeqRef = useRef(0);

  // Ticket generation state
  const [isGeneratingTickets, setIsGeneratingTickets] = useState(false);
  const [ticketGenDone, setTicketGenDone] = useState(false);
  const [showTicketGenConfirm, setShowTicketGenConfirm] = useState(false);
  const [generatedTicketCount, setGeneratedTicketCount] = useState(0);

  // ────────────────────────────────────────────────────────────
  // DATA LOADING
  // ────────────────────────────────────────────────────────────

  const loadData = useCallback(async (showLoading = true) => {
    if (!projectId) return;
    try {
      if (showLoading) {
        setIsLoading(true);
      }
      const [filesRes, statusRes] = await Promise.all([
        listCABFiles(projectId),
        getCABStatus(projectId),
      ]);
      setFiles(filesRes.files);
      setSessionStatus(statusRes.status);
      if (statusRes.phases) {
        setPhases(statusRes.phases);
      }
      if (statusRes.error_info) {
        setErrorInfo(statusRes.error_info);
      }
      if (statusRes.cost) {
        setCostInfo(statusRes.cost);
      }
      if (statusRes.total_duration_seconds != null) {
        setTotalDurationSeconds(statusRes.total_duration_seconds);
      }
      // Load enabled_sections and model_tier from session
      if (statusRes.enabled_sections) {
        setEnabledSections(statusRes.enabled_sections);
      }
      if (statusRes.model_tier) {
        setModelTier(statusRes.model_tier);
      }
      setShareableLinkEnabled(statusRes.readonly_sharable_link_enabled || false);
    } catch (err: any) {
      console.error('Failed to load CAB data:', err);
      setError(extractErrorMessage(err, 'Failed to load data'));
    } finally {
      if (showLoading) {
        setIsLoading(false);
      }
    }
  }, [projectId]);

  const loadPreview = useCallback(async (wip = true) => {
    if (!projectId) return;
    const seq = ++previewSeqRef.current;
    try {
      setIsLoadingPreview(true);
      const html = await getCABHtml(projectId, wip);
      // Only apply the result if no newer request has been issued since
      if (seq === previewSeqRef.current) {
        setPreviewHtml(html);
      }
    } catch (err: any) {
      console.warn('Failed to load CAB preview:', err);
      // Don't show error to user — preview is optional during generation
    } finally {
      if (seq === previewSeqRef.current) {
        setIsLoadingPreview(false);
      }
    }
  }, [projectId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Auto-load preview when generation is completed (final, non-WIP)
  useEffect(() => {
    if (sessionStatus === 'completed' && projectId && !previewHtml) {
      loadPreview(false);
    }
  }, [sessionStatus, projectId]); // eslint-disable-line react-hooks/exhaustive-deps


  // ────────────────────────────────────────────────────────────
  // TICKET GENERATION
  // ────────────────────────────────────────────────────────────

  const handleGenerateTickets = useCallback(async () => {
    if (!projectId) return;
    setIsGeneratingTickets(true);
    setTicketGenDone(false);
    try {
      const result = await generateTicketsFromCAB(projectId);
      setGeneratedTicketCount(result.created);
      setTicketGenDone(true);
    } catch (err) {
      console.error('Failed to generate tickets from CAB:', err);
    } finally {
      setIsGeneratingTickets(false);
    }
  }, [projectId]);

  const handleGenerateTicketsClick = useCallback(async () => {
    if (!projectId) return;
    try {
      const count = await getCabTicketCount(projectId);
      if (count > 0) {
        setShowTicketGenConfirm(true);
      } else {
        handleGenerateTickets();
      }
    } catch (err) {
      console.error('Failed to check CAB ticket count:', err);
      handleGenerateTickets();
    }
  }, [projectId, handleGenerateTickets]);

  // ────────────────────────────────────────────────────────────
  // VERSION HISTORY
  // ────────────────────────────────────────────────────────────

  const loadVersions = useCallback(async () => {
    if (!projectId) return;
    try {
      const list = await listCABVersions(projectId);
      setVersions(list);
    } catch (err) {
      console.warn('Failed to load CAB versions:', err);
      setVersions([]);
    }
  }, [projectId]);

  // Load versions whenever CAB becomes available (or status changes back to idle/cancelled)
  useEffect(() => {
    if (!projectId) return;
    if (sessionStatus === 'completed') {
      loadVersions();
    } else {
      setVersions([]);
      setSelectedVersionTs(null);
      setVersionHtml(null);
    }
  }, [sessionStatus, projectId, loadVersions]);

  // Fetch snapshot HTML when a historical version is selected
  useEffect(() => {
    if (!projectId || selectedVersionTs == null) {
      setVersionHtml(null);
      return;
    }
    const seq = ++versionHtmlSeqRef.current;
    setIsLoadingVersionHtml(true);
    getCABVersionHtml(projectId, selectedVersionTs)
      .then((html) => {
        if (seq === versionHtmlSeqRef.current) {
          setVersionHtml(html);
        }
      })
      .catch((err) => {
        console.error('Failed to load CAB version HTML:', err);
        if (seq === versionHtmlSeqRef.current) {
          setToastMessage({ message: 'Failed to load version preview', variant: 'error' });
          setSelectedVersionTs(null);
        }
      })
      .finally(() => {
        if (seq === versionHtmlSeqRef.current) {
          setIsLoadingVersionHtml(false);
        }
      });
  }, [projectId, selectedVersionTs]);

  const handleConfirmRestore = useCallback(async () => {
    if (!projectId || restoreTarget == null) return;
    try {
      setIsRestoring(true);
      await restoreCABVersion(projectId, restoreTarget);
      setRestoreTarget(null);
      setSelectedVersionTs(null);
      setVersionHtml(null);
      await Promise.all([loadVersions(), loadPreview(false)]);
      setToastMessage({ message: 'Successfully restored selected version', variant: 'success' });
    } catch (err: any) {
      console.error('Failed to restore CAB version:', err);
      setToastMessage({
        message: extractErrorMessage(err, 'Failed to restore version'),
        variant: 'error',
      });
    } finally {
      setIsRestoring(false);
    }
  }, [projectId, restoreTarget, loadVersions, loadPreview]);

  // ────────────────────────────────────────────────────────────
  // WEBSOCKET
  // ────────────────────────────────────────────────────────────

  useCABWebSocket({
    projectId,
    enabled: sessionStatus === 'processing',
    onProgressUpdate: (data) => {
      if (data.phases) setPhases(data.phases);
      if (data.current_phase_name) setCurrentProgressStage(data.current_phase_name);
      if (data.overall_progress?.percentage !== undefined) {
        setOverallPercentage(data.overall_progress.percentage);
      }
      if (data.elapsed_seconds != null) {
        setTotalDurationSeconds(data.elapsed_seconds);
      }
    },
    onPhaseCompleted: (phase) => {
      // Refresh files to get updated parse statuses
      if (phase === 'file_processing' && projectId) {
        listCABFiles(projectId).then((res) => setFiles(res.files)).catch(() => {});
      }
    },
    onSkeletonReady: () => {
      // Load WIP preview as soon as skeleton is ready
      if (projectId) {
        loadPreview();
      }
    },
    onSectionReady: (section) => {
      // Reload preview as each section completes
      if (projectId) {
        loadPreview();
      }
    },
    onCompleted: (data) => {
      setSessionStatus('completed');
      setCurrentProgressStage(null);
      if (data?.total_duration_seconds != null) {
        setTotalDurationSeconds(data.total_duration_seconds);
      }
      loadData(false);
      loadPreview(false);
      onProjectCostUpdate?.();
    },
    onError: (data) => {
      setSessionStatus('error');
      setError(data?.message || 'Generation failed');
    },
    onPaused: (data) => {
      setSessionStatus('paused');
      setCurrentProgressStage(null);
      loadData(false);
      onProjectCostUpdate?.();
    },
    onCancelled: (data) => {
      setSessionStatus('cancelled');
      setCurrentProgressStage(null);
      setPhases(null);
      loadData(false);
      onProjectCostUpdate?.();
    },
  });

  // ────────────────────────────────────────────────────────────
  // POLLING FALLBACK — catches up when WebSocket is slow/missed
  // ────────────────────────────────────────────────────────────

  useEffect(() => {
    if (sessionStatus !== 'processing' || !projectId) return;

    let isMounted = true;
    let lastSkeletonStatus: string | undefined;
    let lastSectionGenerationStatus: string | undefined;

    const pollProgress = async () => {
      try {
        const progress = await getCABProgress(projectId);
        if (!isMounted) return;

        if (progress.phases) {
          // Only update phases if data actually changed (avoids flicker)
          setPhases((prev) => {
            const newJson = JSON.stringify(progress.phases);
            const prevJson = prev ? JSON.stringify(prev) : '';
            return newJson !== prevJson ? progress.phases : prev;
          });

          // Check if skeleton just became ready
          const skeletonStatus = progress.phases.skeleton_assemble?.status;
          if (skeletonStatus === 'completed' && lastSkeletonStatus !== 'completed') {
            loadPreview(); // Load WIP preview with skeleton
          }
          lastSkeletonStatus = skeletonStatus;

          // Check if section generation is running — refresh preview periodically
          const sectionGenStatus = progress.phases.section_generation?.status;
          if (sectionGenStatus === 'running' && previewHtml) {
            loadPreview(); // Refresh to pick up newly completed sections
          }
          lastSectionGenerationStatus = sectionGenStatus;
        }
        if (progress.current_phase_name) {
          setCurrentProgressStage((prev) => prev !== progress.current_phase_name ? progress.current_phase_name : prev);
        }
        if (progress.overall_progress?.percentage !== undefined) {
          setOverallPercentage((prev) => prev !== progress.overall_progress.percentage ? progress.overall_progress.percentage : prev);
        }

        // Detect terminal states from the backend
        if (progress.status === 'completed') {
          setSessionStatus('completed');
          setCurrentProgressStage(null);
          loadData(false);
          loadPreview(false); // Final preview (non-WIP)
          onProjectCostUpdate?.();
          return; // stop polling
        }
        if (progress.status === 'error') {
          setSessionStatus('error');
          loadData(false);
          return; // stop polling
        }
        if (progress.status === 'paused' || progress.status === 'cancelled') {
          setSessionStatus(progress.status);
          return; // stop polling
        }
      } catch (err) {
        console.warn('CAB progress poll failed:', err);
      }
    };

    // Poll immediately on mount, then every 30 seconds (WebSocket is the primary channel)
    pollProgress();
    const intervalId = setInterval(pollProgress, 30000);

    return () => {
      isMounted = false;
      clearInterval(intervalId);
    };
  }, [sessionStatus, projectId, previewHtml]); // eslint-disable-line react-hooks/exhaustive-deps

  // ────────────────────────────────────────────────────────────
  // FILE HANDLERS
  // ────────────────────────────────────────────────────────────

  /** Safely extract a human-readable error string from an Axios error. */
  const extractErrorMessage = (err: any, fallback: string): string => {
    const detail = err?.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    // FastAPI 422 returns detail as an array of validation-error objects
    if (Array.isArray(detail)) {
      const msgs = detail.map((d: any) => d?.msg).filter(Boolean);
      return msgs.length > 0 ? msgs.join('; ') : fallback;
    }
    return fallback;
  };

  const handleFilesSelected = useCallback(async (files: File[]) => {
    if (!projectId) return;

    // ── Pre-flight: validate extension + filename length before any network call ──
    const validationErrors = validateCABFilesForUpload(files);
    if (validationErrors.length > 0) {
      setError(validationErrors.join('\n'));
      return;
    }

    setIsUploading(true);
    setError(null);

    // Create initial upload states for all files with AbortControllers
    const initialStates: FileUploadState[] = files.map((file, idx) => ({
      tempId: `temp-${Date.now()}-${idx}`,
      file,
      status: 'pending',
      progress: 0,
      abortController: new AbortController(),
    }));

    // Immediately show files in UI with "pending" status
    setUploadingFiles(initialStates);

    // Upload all files in parallel
    const uploadPromises = initialStates.map(async (uploadState) => {
      try {
        // Update status to "uploading"
        setUploadingFiles((prev) =>
          prev.map((s) =>
            s.tempId === uploadState.tempId
              ? { ...s, status: 'uploading' as const }
              : s
          )
        );

        const result = await uploadCABFile(
          projectId,
          uploadState.file,
          (percent) => {
            // Update individual file progress
            setUploadingFiles((prev) =>
              prev.map((s) =>
                s.tempId === uploadState.tempId
                  ? { ...s, progress: percent }
                  : s
              )
            );
          },
          uploadState.abortController
        );

        // Mark as completed
        setUploadingFiles((prev) =>
          prev.map((s) =>
            s.tempId === uploadState.tempId
              ? {
                  ...s,
                  status: 'completed' as const,
                  progress: 100,
                  fileId: result.file_id,
                }
              : s
          )
        );

        return { success: true, result };
      } catch (err: any) {
        // Check if error is due to abort
        if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') {
          console.log('File upload cancelled:', uploadState.file.name);
          return { success: false, cancelled: true };
        }

        console.error('File upload failed:', err);
        const errorMsg = extractErrorMessage(err, 'Upload failed');

        // Mark as error
        setUploadingFiles((prev) =>
          prev.map((s) =>
            s.tempId === uploadState.tempId
              ? { ...s, status: 'error' as const, error: errorMsg }
              : s
          )
        );

        return { success: false, error: err };
      }
    });

    // Wait for all uploads to complete (or fail)
    const results = await Promise.allSettled(uploadPromises);

    // Calculate overall progress
    const successCount = results.filter(
      (r) => r.status === 'fulfilled' && r.value.success
    ).length;
    const cancelledCount = results.filter(
      (r) => r.status === 'fulfilled' && r.value.cancelled
    ).length;
    const failCount = results.length - successCount - cancelledCount;

    // Show error if any uploads failed (but not if they were cancelled)
    if (failCount > 0) {
      const failedDetails = results
        .map((r) => (r.status === 'fulfilled' && !r.value.success && !r.value.cancelled ? r.value : null))
        .filter(Boolean)
        .map((r: any) => extractErrorMessage(r.error, 'Upload failed'));
      const uniqueReasons = [...new Set(failedDetails)];
      setError(`${failCount} file(s) failed to upload. ${uniqueReasons.join(' | ')}`);
    }

    // Refresh file list from server, then clear uploading state so
    // there's no moment where both lists show the same file.
    await loadData(false);

    // Server list is now in `files[]` — safe to clear uploading entries.
    // Keep error entries briefly so user can see what failed.
    setUploadingFiles((prev) =>
      prev.filter((s) => s.status === 'error')
    );
    setIsUploading(false);

    if (failCount > 0) {
      setTimeout(() => {
        setUploadingFiles([]);
      }, 3000);
    }
  }, [projectId, loadData, extractErrorMessage]);

  const handleDeleteFile = useCallback(async (fileId: string) => {
    if (!projectId) return;
    try {
      setError(null);
      await deleteCABFile(projectId, fileId);
      setFiles((prev) => prev.filter((f) => f.file_id !== fileId));
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to delete file'));
    }
  }, [projectId]);

  const handleDownloadFile = useCallback(async (fileId: string, fileName: string) => {
    if (!projectId) return;
    try {
      setError(null);
      await downloadCABFile(projectId, fileId, fileName);
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to download file'));
    }
  }, [projectId]);

  const handleDeleteAll = useCallback(async () => {
    if (!projectId) return;
    try {
      setError(null);

      // STEP 1: Cancel all in-flight uploads atomically
      uploadingFiles.forEach((uploadState) => {
        if (uploadState.abortController && uploadState.status === 'uploading') {
          uploadState.abortController.abort();
        }
      });

      // STEP 2: Clear upload state immediately (UX feedback)
      setUploadingFiles([]);
      setIsUploading(false);

      // STEP 3: Small delay to ensure abort signals propagate through network stack
      await new Promise(resolve => setTimeout(resolve, 100));

      // STEP 4: Now safely delete everything from backend
      await deleteCAB(projectId);

      // STEP 5: Clear remaining state
      setFiles([]);
      setSessionStatus('idle');
      setPhases(null);
      setPreviewHtml(null);
      setCostInfo(null);
      setTotalDurationSeconds(null);
      setEnabledSections(DEFAULT_ENABLED_SECTIONS);
      setModelTier(DEFAULT_MODEL_TIER);
      onProjectCostUpdate?.();
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to delete'));
    }
  }, [projectId, uploadingFiles, onProjectCostUpdate]);

  // ────────────────────────────────────────────────────────────
  // GENERATION HANDLERS
  // ────────────────────────────────────────────────────────────

  const handleGenerate = useCallback(async () => {
    if (!projectId) return;
    if (enabledSections.length === 0) {
      setError('Select at least one section to generate');
      return;
    }
    try {
      setError(null);
      await generateCAB(projectId, { enabled_sections: enabledSections, model_tier: modelTier });
      setSessionStatus('processing');
      setPhases(null);
      setOverallPercentage(0);
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to start generation'));
    }
  }, [projectId, enabledSections, modelTier]);

  const handleRegenerate = useCallback(async () => {
    if (!projectId) return;
    try {
      setError(null);
      setErrorInfo(null);
      setSessionStatus('idle');
      setPhases(null);
      setPreviewHtml(null);
      setTotalDurationSeconds(null);
      await regenerateCAB(projectId, { enabled_sections: enabledSections, model_tier: modelTier });
      setSessionStatus('processing');
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to regenerate'));
    }
  }, [projectId, enabledSections, modelTier]);

  const handlePause = useCallback(async () => {
    if (!projectId) return;
    try {
      await pauseCAB(projectId);
      setSessionStatus('paused');
      onProjectCostUpdate?.();
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to pause'));
    }
  }, [projectId, onProjectCostUpdate]);

  const handleStop = useCallback(async () => {
    if (!projectId) return;
    try {
      await cancelCAB(projectId);
      setSessionStatus('cancelled');
      setPhases(null);
      setPreviewHtml(null);
      setOverallPercentage(0);
      setCurrentProgressStage(null);
      setTotalDurationSeconds(null);
      onProjectCostUpdate?.();
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to stop'));
    }
  }, [projectId, onProjectCostUpdate]);

  const handleResume = useCallback(async () => {
    if (!projectId) return;
    try {
      setError(null);
      setErrorInfo(null);
      await resumeCAB(projectId);
      setSessionStatus('processing');
      onProjectCostUpdate?.();
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to resume'));
    }
  }, [projectId, onProjectCostUpdate]);

  // ────────────────────────────────────────────────────────────
  // EDIT MODE HANDLERS
  // ────────────────────────────────────────────────────────────

  const enterEditMode = useCallback(async () => {
    if (!projectId) return;
    try {
      // `isEnteringEditMode` covers the entire perceived operation:
      // network fetch + iframe HTML parse + edit-script init. We only flip
      // it off when the iframe sends `cab-iframe-ready` (see message
      // handler below). `isLoadingEditable` covers just the network leg
      // and is used by the Edit button's spinner.
      setIsLoadingEditable(true);
      setIsEnteringEditMode(true);
      setEditError(null);

      const [html, versionRes] = await Promise.all([
        getCABHtmlEditable(projectId),
        getCABVersion(projectId),
      ]);

      // Toggle the toolbar to Save/Discard first and let the browser paint that
      // frame cleanly. Then, on the next animation frame, swap the iframe's
      // srcdoc — this triggers a multi-second parse of the editable HTML that
      // would otherwise block the parent's first paint and leave the new
      // buttons looking unstyled until the iframe settled.
      setCabVersion(versionRes.version);
      setIsEditMode(true);
      setIsLoadingEditable(false);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setEditableHtml(html);
        });
      });
    } catch (err: any) {
      console.error('Failed to enter edit mode:', err);
      setEditError(extractErrorMessage(err, 'Failed to load editable version'));
      setIsLoadingEditable(false);
      setIsEnteringEditMode(false);
    }
  }, [projectId]);

  const exitEditMode = useCallback(() => {
    // The iframe has to re-parse the (large) read-only preview HTML when
    // we drop the editable HTML — that re-parse can take a noticeable
    // moment on big CABs and looks like a freeze without feedback.
    // We show the overlay until iframe.onload fires (handled inline) or
    // a 4s safety timeout, whichever comes first.
    setIsExitingEditMode(true);
    setIsEditMode(false);
    setEditableHtml(null);
    setCabVersion(null);
    setEditError(null);
    if (exitTimeoutRef.current) {
      window.clearTimeout(exitTimeoutRef.current);
    }
    exitTimeoutRef.current = window.setTimeout(() => {
      setIsExitingEditMode(false);
      exitTimeoutRef.current = null;
    }, 4000);
  }, []);

  useEffect(() => {
    return () => {
      if (exitTimeoutRef.current) {
        window.clearTimeout(exitTimeoutRef.current);
      }
    };
  }, []);

  const handleIframeSave = useCallback(async (ops: PatchOp[]) => {
    if (!projectId || !cabVersion) return;

    // Smooth save flow:
    //   1. isSaving stays true through PATCH + preview reload (single perceived
    //      operation from the user's POV).
    //   2. We only tear down editableHtml AFTER the new previewHtml is in state,
    //      so the iframe never flashes stale or empty content between phases.
    //   3. The in-pane overlay shows "Saving..." then "Loading latest..." over
    //      the iframe throughout — see displayHtml + overlay logic below.
    try {
      setIsSaving(true);
      setEditError(null);
      const result = await patchCAB(projectId, { baseVersion: cabVersion, ops });

      if (!result.success) {
        const errorMsg = result.errors?.length > 0 ? result.errors.join(' | ') : 'Failed to save changes';
        setToastMessage({ message: errorMsg, variant: 'error' });
        return;
      }

      // PATCH succeeded — fetch fresh preview + versions BEFORE leaving edit mode
      // so the user sees a continuous loading state instead of a flash of old content.
      await Promise.all([loadPreview(false), loadVersions()]);

      // Now swap out of edit mode in a single paint to avoid layout flicker.
      // Mark the iframe re-parse window so the overlay stays visible until
      // the read-only iframe's onLoad fires (or 4s safety timeout).
      setIsExitingEditMode(true);
      if (exitTimeoutRef.current) {
        window.clearTimeout(exitTimeoutRef.current);
      }
      exitTimeoutRef.current = window.setTimeout(() => {
        setIsExitingEditMode(false);
        exitTimeoutRef.current = null;
      }, 4000);
      setIsEditMode(false);
      setEditableHtml(null);
      setCabVersion(null);
      setToastMessage({ message: 'Changes saved successfully', variant: 'success' });
    } catch (err: any) {
      console.error('Failed to save CAB edits:', err);
      const errMsg = extractErrorMessage(err, 'Failed to save changes');
      setToastMessage({ message: errMsg, variant: 'error' });
    } finally {
      setIsSaving(false);
    }
  }, [projectId, cabVersion, loadPreview, loadVersions]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent<CABIframeMessage>) => {
      const data = event.data;
      if (!data || typeof data !== 'object' || !data.type) return;

      switch (data.type) {
        case 'cab-save':
          if (data.ops && Array.isArray(data.ops)) {
            handleIframeSave(data.ops);
          }
          break;
        case 'cab-save-noop':
          // User clicked Save without making any changes — skip the API
          // call and just leave edit mode (Save button is intentionally
          // always-enabled to avoid the "why is this disabled?" UX).
          exitEditMode();
          break;
        case 'cab-iframe-ready':
          if (isEditMode && iframeRef.current?.contentWindow) {
            iframeRef.current.contentWindow.postMessage({ type: 'cab-set-mode', editing: true }, '*');
          }
          // Editable iframe is now fully parsed and the edit script is
          // wired up — safe to drop the entering-edit-mode overlay.
          setIsEnteringEditMode(false);
          break;
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [handleIframeSave, isEditMode, exitEditMode]);

  // ────────────────────────────────────────────────────────────
  // COMPUTED
  // ────────────────────────────────────────────────────────────

  const hasFiles = files.length > 0 || uploadingFiles.length > 0;
  const isProcessing = sessionStatus === 'processing';
  const hasGenerationStarted = sessionStatus === 'processing' || sessionStatus === 'paused' || sessionStatus === 'completed' || sessionStatus === 'error';
  const canGenerate = files.length > 0 && !isProcessing && !isUploading && enabledSections.length > 0;
  const showProgressSidebar = isProcessing || sessionStatus === 'paused' || sessionStatus === 'error';
  const isViewingSnapshot = selectedVersionTs != null;
  const canEdit = sessionStatus === 'completed' && !isProcessing && !isViewingSnapshot;

  // Determine preview empty state variant
  // Keep showing 'no-files' until generation starts to avoid message flicker
  const previewVariant: 'no-files' | 'ready-to-generate' | 'processing' = isProcessing
    ? 'processing'
    : 'no-files';

  // Select which HTML to render in the iframe.
  // Snapshot view takes precedence; edit mode is disabled while viewing a snapshot.
  const displayHtml = useMemo(() => {
    if (isViewingSnapshot) return versionHtml;
    if (isEditMode && editableHtml) return editableHtml;
    return previewHtml;
  }, [isViewingSnapshot, versionHtml, isEditMode, editableHtml, previewHtml]);

  // ────────────────────────────────────────────────────────────
  // RENDER
  // ────────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full w-full">
        <div className="w-8 h-8 border-2 border-border-secondary border-t-bg-brand-solid rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex h-full gap-bg-v2-xl overflow-hidden w-full">
      {/* ═══════ LEFT: Files + Controls + Progress Sidebar ═══════ */}
      <div className="flex-[4] min-w-0 flex gap-bg-v2-xl">
        {/* Main content panel */}
        <div className="flex-1 min-w-0 flex flex-col bg-bg-primary rounded-bg-v2-lg container-shadow-sm overflow-hidden">
          {/* Header — min-h matches the preview panel header (icon buttons add height) */}
          <div className="px-bg-v2-xl py-bg-v2-lg border-b border-border-secondary flex items-center min-h-[56px] shrink-0">
            <Text variant="text-md" weight="semibold" className="text-text-primary">
              Upload Files
            </Text>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-bg-v2-xl">
            <IfHasPermission
              permissions={permissions}
              requires={Permissions.UpdateProjectPermission}
            >
              <CABFileUpload
                variant={hasFiles ? 'compact' : 'large'}
                onFilesSelected={handleFilesSelected}
                disabled={hasGenerationStarted}
                isUploading={isUploading}
              />
            </IfHasPermission>
            <CABFileList
              files={files}
              uploadingFiles={uploadingFiles}
              onDeleteFile={handleDeleteFile}
              onDownloadFile={handleDownloadFile}
              disabled={hasGenerationStarted}
              permissions={permissions}
            />
            {hasFiles && (
              <>
                <CABSectionSelector
                  enabledSections={enabledSections}
                  onChange={setEnabledSections}
                  disabled={hasGenerationStarted}
                  readOnly={!permissions.includes(Permissions.UpdateProjectPermission)}
                />
                <CABModelSelector
                  selectedTier={modelTier}
                  onChange={setModelTier}
                  disabled={hasGenerationStarted}
                  readOnly={!permissions.includes(Permissions.UpdateProjectPermission)}
                />
              </>
            )}
            {error && (
              <div className="mt-bg-v2-md p-bg-v2-md bg-bg-error-primary rounded-bg-v2-md">
                <Text variant="text-xs" className="text-text-error-primary">{error}</Text>
              </div>
            )}
          </div>

          {/* Cost panel */}
          {costInfo && (
            <CABCostPanel
              cost={costInfo}
              visible={sessionStatus === 'completed' || sessionStatus === 'error' || (costInfo.total_usd > 0)}
              totalDurationSeconds={totalDurationSeconds}
            />
          )}

          {/* Action bar */}
          <CABActionBar
            status={sessionStatus}
            canGenerate={canGenerate}
            hasFiles={hasFiles}
            onGenerate={handleGenerate}
            onPause={handlePause}
            onStop={handleStop}
            onDeleteAll={handleDeleteAll}
            onResume={handleResume}
            isUploading={isUploading}
            permissions={permissions}
            platformType={platformType}
            onGenerateDesign={async () => {
              if (!projectId || !onGenerateDesign) return;
              onGenerateDesign();
            }}
          />
        </div>

        {/* Progress sidebar */}
        <CABProgressSidebar
          phases={phases}
          visible={showProgressSidebar}
          sessionStatus={sessionStatus}
        />
      </div>

      {/* ═══════ RIGHT: Preview Panel ═══════ */}
      <div className="flex-[6] min-w-0 flex flex-col bg-bg-primary rounded-bg-v2-lg container-shadow-sm overflow-hidden">
        {/* Preview header */}
        <div className="px-bg-v2-xl py-bg-v2-lg border-b border-border-secondary flex justify-between items-center min-h-[56px] shrink-0">
          <div className="flex items-center gap-bg-v2-sm">
            <Text variant="text-md" weight="semibold" className="text-text-primary">
              Common Application Blueprint Preview
            </Text>
            {isEditMode && (
              <span className="inline-flex items-center gap-bg-v2-xs bg-utility-purple-100 text-utility-purple-700 px-bg-v2-sm py-bg-v2-xxs rounded-bg-v2-sm text-bg-v2-xs font-medium">
                <span className="w-1.5 h-1.5 rounded-full bg-utility-purple-700"></span>
                Editing
              </span>
            )}
            {isViewingSnapshot && !isEditMode && (
              <span className="inline-flex items-center gap-bg-v2-xs bg-utility-blue-100 text-utility-blue-700 px-bg-v2-sm py-bg-v2-xxs rounded-bg-v2-sm text-bg-v2-xs font-medium">
                <span className="w-1.5 h-1.5 rounded-full bg-utility-blue-700"></span>
                Viewing previous version
              </span>
            )}
          </div>
          <div className="flex gap-bg-v2-sm">
            {isEditMode ? (
              // Keyed fragment so React unmounts these buttons (and their
              // tooltips) when leaving edit mode, instead of reconciling
              // them with the read-only buttons in the same slot — which
              // would otherwise carry over Tooltip state (isVisible/timers)
              // onto an unrelated button (e.g. Save → Open in New Tab).
              <React.Fragment key="cab-toolbar-edit">
                <Button
                  type="button"
                  label={isSaving ? 'Saving...' : 'Save'}
                  onClick={() => {
                    iframeRef.current?.contentWindow?.postMessage({ type: 'cab-request-save' }, '*');
                  }}
                  variant="primary"
                  size="small"
                  loading={isSaving}
                  title='Save Changes'
                  disabled={isSaving}
                />
                  <Button
                    type="button"
                    label={<Icon name={IconName.Close} size="sm" inheritColor />}
                    onClick={() => {
                      iframeRef.current?.contentWindow?.postMessage({ type: 'cab-discard' }, '*');
                      exitEditMode();
                    }}
                    variant="outline"
                    size="small"
                    title='Discard Changes'
                    disabled={isSaving}
                  />
              </React.Fragment>
            ) : (
              <React.Fragment key="cab-toolbar-view">
                {sessionStatus === 'completed' && !isViewingSnapshot && (
                  <IfHasPermission permissions={permissions} requires={Permissions.UpdateProjectPermission}>
                    <button
                      type="button"
                      disabled={isGeneratingTickets}
                      onClick={handleGenerateTicketsClick}
                      className="
                        px-bg-v2-lg py-bg-v2-sm border border-border-primary rounded-bg-v2-md
                        bg-bg-primary text-text-secondary text-bg-v2-xs font-bg-v2-medium
                        flex items-center gap-bg-v2-xs transition-colors
                        hover:bg-bg-primary-hover whitespace-nowrap
                        disabled:opacity-40 disabled:cursor-not-allowed
                      "
                    >
                      {isGeneratingTickets
                        ? <Icon name={IconName.Loading} size="sm" inheritColor />
                        : <Icon name={IconName.Tickets} size="sm" inheritColor />}
                      {isGeneratingTickets ? 'Generating...' : 'Generate Tickets'}
                    </button>
                  </IfHasPermission>
                )}
                {sessionStatus === 'completed' && versions.length > 0 && (
                  <CABVersionHistory
                    versions={versions}
                    selectedTimestampMs={selectedVersionTs}
                    onSelectVersion={setSelectedVersionTs}
                    onClearSelection={() => setSelectedVersionTs(null)}
                    onRestoreVersion={(ts) => setRestoreTarget(ts)}
                    isRestoring={isRestoring}
                    permissions={permissions}
                  />
                )}
                <Button
                  type="button"
                  label={<Icon name={IconName.ExternalLink} size="sm" inheritColor />}
                  onClick={() => {
                    if (!projectId) return;
                    window.open(`/project/${projectId}/cab`, '_blank');
                  }}
                  variant="outline"
                  size="very-small"
                  className="!w-8 !h-8 !p-0 !min-w-0"
                  disabled={sessionStatus !== 'completed' || isViewingSnapshot}
                  title="Open in New Tab"
                  tooltipPosition="bottom"
                />
                <IfHasPermission permissions={permissions} requires={Permissions.UpdateProjectPermission}>
                  <Button
                    type="button"
                    label={<Icon name={IconName.Share} size="sm" inheritColor />}
                    onClick={() => setShowShareModal(true)}
                    variant="outline"
                    size="very-small"
                    className="!w-8 !h-8 !p-0 !min-w-0"
                    disabled={sessionStatus !== 'completed' || isViewingSnapshot}
                    title="Share CAB"
                    tooltipPosition="bottom"
                  />
                </IfHasPermission>
                <IfHasPermission permissions={permissions} requires={Permissions.UpdateProjectPermission}>
                  <Button
                    type="button"
                    label={isLoadingEditable ? null : <Icon name={IconName.Edit} size="sm" inheritColor />}
                    onClick={enterEditMode}
                    variant="outline"
                    size="very-small"
                    className="!w-8 !h-8 !p-0 !min-w-0"
                    disabled={!canEdit || isLoadingEditable}
                    loading={isLoadingEditable}
                    title="Edit CAB"
                    tooltipPosition="bottom"
                  />
                </IfHasPermission>
                <Button
                  type="button"
                  label={isDownloadingMarkdown ? null : <Icon name={IconName.FileDownload} size="sm" inheritColor />}
                  onClick={async () => {
                    if (!projectId || isDownloadingMarkdown) return;
                    setIsDownloadingMarkdown(true);
                    try {
                      await downloadCABMarkdown(projectId);
                    } catch (err) {
                      console.error('Failed to download CAB Markdown:', err);
                    } finally {
                      setIsDownloadingMarkdown(false);
                    }
                  }}
                  variant="outline"
                  size="very-small"
                  className="!w-8 !h-8 !p-0 !min-w-0"
                  disabled={sessionStatus !== 'completed' || isViewingSnapshot}
                  loading={isDownloadingMarkdown}
                  title={isDownloadingMarkdown ? 'Downloading…' : 'Download Markdown'}
                  tooltipPosition="bottom"
                />
                <Button
                  type="button"
                  label={isDownloadingPdf ? null : <Icon name={IconName.PdfDownload} size="sm" inheritColor />}
                  onClick={async () => {
                    if (!projectId || isDownloadingPdf) return;
                    setIsDownloadingPdf(true);
                    try {
                      await downloadCABPdf(projectId);
                    } catch (err) {
                      console.error('Failed to download CAB PDF:', err);
                    } finally {
                      setIsDownloadingPdf(false);
                    }
                  }}
                  variant="outline"
                  size="very-small"
                  className="!w-8 !h-8 !p-0 !min-w-0"
                  disabled={sessionStatus !== 'completed' || isViewingSnapshot}
                  loading={isDownloadingPdf}
                  title={isDownloadingPdf ? 'Generating PDF…' : 'Download PDF'}
                  tooltipPosition="bottom"
                />
              </React.Fragment>
            )}
          </div>
        </div>

        {/* Preview content */}
        <div className="flex-1 overflow-hidden relative">
          {displayHtml ? (
            <iframe
              ref={iframeRef}
              srcDoc={displayHtml}
              className="w-full h-full border-0"
              title={isEditMode ? 'CAB Edit' : 'CAB Preview'}
              sandbox="allow-scripts allow-same-origin"
              onLoad={() => {
                // Read-only iframe finished re-parsing after we left edit mode.
                // (For the editable iframe, `cab-iframe-ready` is what we wait
                // on — the script runs after this, so onLoad is too early.)
                if (isExitingEditMode) {
                  setIsExitingEditMode(false);
                  if (exitTimeoutRef.current) {
                    window.clearTimeout(exitTimeoutRef.current);
                    exitTimeoutRef.current = null;
                  }
                }
              }}
            />
          ) : isLoadingPreview || isLoadingEditable || isLoadingVersionHtml ? (
            <div className="flex items-center justify-center h-full">
              <div className="w-8 h-8 border-2 border-border-secondary border-t-bg-brand-solid rounded-full animate-spin" />
            </div>
          ) : sessionStatus === 'error' && errorInfo ? (
            <CABErrorState
              error={errorInfo}
              onResume={handleResume}
              onRegenerate={handleRegenerate}
            />
          ) : (
            <CABEmptyState
              variant={previewVariant}
              fileCount={files.length}
            />
          )}

          {/* Loading overlay layered over the preview.
              Shown during: entering edit mode, leaving edit mode, save in
              progress, fetching a snapshot version, or refreshing the
              latest CAB after a save. Sits inside the preview container
              (absolute) so it covers only the iframe area. */}
          {(displayHtml || isEnteringEditMode) && (
            isSaving ||
            isLoadingVersionHtml ||
            isEnteringEditMode ||
            isExitingEditMode ||
            (isLoadingPreview && !isEditMode)
          ) && (
            <div className="absolute inset-0 z-30 flex items-center justify-center bg-black/20">
              <div className="bg-bg-primary rounded-bg-v2-md px-bg-v2-lg py-bg-v2-md container-shadow-md flex items-center gap-bg-v2-sm">
                <Icon name={IconName.Loading} size="sm" spin inheritColor />
                <Text variant="text-sm" color="secondary">
                  {isSaving
                    ? (isLoadingPreview ? 'Loading latest version...' : 'Saving changes...')
                    : isLoadingVersionHtml
                      ? 'Loading version preview...'
                      : isEnteringEditMode
                        ? (isLoadingEditable ? 'Loading editor...' : 'Preparing editor...')
                        : isExitingEditMode
                          ? 'Preparing viewer...'
                          : 'Loading latest version...'}
                </Text>
              </div>
            </div>
          )}

          {/* Ticket generation progress overlay */}
          {(isGeneratingTickets || ticketGenDone) && !isEditMode && (
            <div className="absolute inset-0 z-40 flex items-center justify-center bg-black/30">
              <div className="bg-bg-primary rounded-bg-v2-lg px-bg-v2-2xl py-bg-v2-xl container-shadow-lg flex flex-col items-center gap-bg-v2-md w-[360px]">
                {ticketGenDone ? (
                  <>
                    <div className="w-10 h-10 rounded-full bg-utility-success-100 flex items-center justify-center">
                      <Icon name={IconName.CheckCircle} size="md" className="!text-utility-success-600" inheritColor />
                    </div>
                    <Text variant="text-md" weight="semibold" color="primary">Tickets Created</Text>
                    <Text variant="text-sm" color="secondary" className="text-center">
                      {generatedTicketCount} tickets have been created from this Blueprint. Switch to the Tickets tab to view them.
                    </Text>
                    <div className="flex items-center gap-bg-v2-sm w-full pt-bg-v2-xs">
                      {onNavigateToTickets && (
                        <button
                          type="button"
                          onClick={() => { setTicketGenDone(false); onNavigateToTickets(); }}
                          className="flex-1 bg-bg-brand-solid text-text-white px-bg-v2-lg py-bg-v2-md rounded-bg-v2-md font-bg-v2-semibold text-bg-v2-sm transition-colors hover:bg-bg-brand-solid-hover"
                        >
                          View Tickets
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => setTicketGenDone(false)}
                        className="flex-1 border border-border-primary bg-bg-primary text-text-secondary px-bg-v2-lg py-bg-v2-md rounded-bg-v2-md text-bg-v2-sm font-bg-v2-medium transition-colors hover:bg-bg-primary-hover"
                      >
                        Dismiss
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="w-10 h-10 rounded-full bg-bg-brand-primary flex items-center justify-center">
                      <Icon name={IconName.Tickets} size="md" className="!text-text-brand-primary" inheritColor />
                    </div>
                    <Text variant="text-md" weight="semibold" color="primary">Creating Tickets from Blueprint</Text>
                    <Text variant="text-sm" color="secondary" className="text-center">
                      Analysing Blueprint sections and creating tickets...
                    </Text>
                    <div className="w-8 h-8 border-2 border-border-secondary border-t-bg-brand-solid rounded-full animate-spin mt-bg-v2-xs" />
                  </>
                )}
              </div>
            </div>
          )}

          {editError && (
            <div className="absolute top-2 left-2 bg-red-100 text-red-800 px-3 py-1 rounded text-xs font-medium shadow-sm">
              {editError}
            </div>
          )}
          {/* Toast notification for save success/error */}
          {toastMessage && (
            <div className="absolute bottom-4 left-4 z-50 max-w-sm shadow-lg">
              <FormAlert
                message={toastMessage.message}
                variant={toastMessage.variant}
                autoDismiss
                autoDismissTimeout={4000}
                onDismiss={() => setToastMessage(null)}
              />
            </div>
          )}
        </div>
      </div>

      {/* Share Modal */}
      {showShareModal && (
        <CABShareModal
          projectId={projectId!}
          shareableLinkEnabled={shareableLinkEnabled}
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
          onSave={async (enabled: boolean) => {
            const result = await updateCABShareSettings(projectId!, enabled);
            setShareableLinkEnabled(result.readonly_sharable_link_enabled);
          }}
        />
      )}

      {/* Restore Version Confirmation Modal */}
      <Modal
        isOpen={restoreTarget != null}
        onClose={() => { if (!isRestoring) setRestoreTarget(null); }}
        title="Restore this version?"
        size="sm"
        onSubmit={handleConfirmRestore}
        onCancel={() => { if (!isRestoring) setRestoreTarget(null); }}
        submitLabel={isRestoring ? 'Restoring...' : 'Restore'}
        cancelLabel="Cancel"
        submitLoading={isRestoring}
      >
        <Text color="secondary">
          {restoreTarget != null
            ? `This will replace the current Common Application Blueprint with the version from ${new Date(restoreTarget).toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}. All versions created after this one will be permanently deleted. This action cannot be undone.`
            : ''}
        </Text>
      </Modal>

      {/* Ticket Generation Confirmation Modal */}
      <Modal
        isOpen={showTicketGenConfirm}
        onClose={() => setShowTicketGenConfirm(false)}
        title="Regenerate Tickets?"
        size="sm"
        onSubmit={() => {
          setShowTicketGenConfirm(false);
          handleGenerateTickets();
        }}
        onCancel={() => setShowTicketGenConfirm(false)}
        submitLabel="Regenerate"
        cancelLabel="Cancel"
      >
        <Text color="secondary">
          Tickets already exist from this Blueprint. Regenerating will replace all previously generated CAB tickets. Are you sure?
        </Text>
      </Modal>

    </div>
  );
};

export default CABPage;
